import { datadogRum } from '@datadog/browser-rum'
import getEnvironment from '@/utils/env'
import { onConsentChange, hasConsent, removeCookies } from './onetrust'

type DatadogRum = typeof datadogRum

// Track initialization state
let isInitialized = false
let cachedUserPayload: UserPayload | null = null
let cachedAccountPayload: AccountPayload | null = null

interface UserPayload {
  id: string
  name?: string
  email?: string
  [key: string]: string | boolean | undefined
}

interface AccountPayload {
  id: string
  name?: string
  [key: string]: string | boolean | undefined
}

onConsentChange('analytics', {
  onAccepted: startDatadog,
  onDenied: stopDatadog,
})

/**
 * Get the DataDog RUM instance if initialized and consent granted.
 */
export function getDatadogRum(): DatadogRum | null {
  if (!isInitialized || !hasConsent('analytics')) {
    return null
  }
  return datadogRum
}

/**
 * Set user and account context in DataDog RUM.
 * If DataDog is not yet initialized, the payloads are cached and applied once consent is granted.
 */
export function setDatadogUserAndAccount(user: UserPayload | null, account?: AccountPayload | null): void {
  if (!isInitialized) {
    // Cache for when DataDog initializes
    cachedUserPayload = user ?? null
    cachedAccountPayload = account ?? null
    return
  }

  const currentUser = datadogRum.getUser()
  const currentAccount = datadogRum.getAccount()
  const shouldRestartSession = currentUser.id != null && currentUser.id !== user?.id
  const shouldClearAccount = currentAccount.id != null && currentAccount.id !== account?.id

  if (shouldRestartSession) {
    datadogRum.stopSession()
    datadogRum.stopSessionReplayRecording()
    datadogRum.clearUser()

    if (shouldClearAccount) {
      datadogRum.clearAccount()
    }
  }

  if (user != null) datadogRum.setUser(user)
  else datadogRum.clearUser()

  if (account != null) datadogRum.setAccount(account)
  else datadogRum.clearAccount()

  if (shouldRestartSession) {
    datadogRum.startSessionReplayRecording()
  }
}

// --- Internal helpers ---

function startDatadog(): void {
  const environment = getEnvironment()
  const forceEnable = import.meta.env.VITE_TEST_EXTERNAL_SCRIPTS === 'true'

  // Only enable in production and test environments (unless forced)
  if (!forceEnable && (environment == null || !['production', 'test'].includes(environment.id))) {
    return
  }

  if (isInitialized) {
    // Already initialized, just grant consent
    datadogRum.setTrackingConsent('granted')
    datadogRum.startSessionReplayRecording()

    if (cachedUserPayload != null || cachedAccountPayload != null) {
      setDatadogUserAndAccount(cachedUserPayload, cachedAccountPayload)
      cachedUserPayload = null
      cachedAccountPayload = null
    }

    return
  }

  datadogRum.init({
    applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID ?? '',
    clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN ?? '',
    site: 'datadoghq.eu',
    service: 'ada-customer-ui',
    // Environment-driven (legacy datadog.js hardcoded 'production' for all
    // customer traffic — intentional improvement)
    env: environment?.id ?? 'localhost',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    trackViewsManually: true,
    defaultPrivacyLevel: 'allow',
    trackingConsent: 'granted',
    allowedTracingUrls: [{ match: import.meta.env.VITE_APP_API_ROOT, propagatorTypes: ['tracecontext'] }],
  })

  datadogRum.startSessionReplayRecording()
  isInitialized = true

  // Apply cached user/account data if available
  if (cachedUserPayload != null || cachedAccountPayload != null) {
    setDatadogUserAndAccount(cachedUserPayload, cachedAccountPayload)
    cachedUserPayload = null
    cachedAccountPayload = null
  }
}

function stopDatadog(): void {
  if (!isInitialized) return

  cachedUserPayload = datadogRum.getUser() as UserPayload | null
  cachedAccountPayload = datadogRum.getAccount() as AccountPayload | null

  datadogRum.stopSession()
  datadogRum.stopSessionReplayRecording()
  datadogRum.clearUser()
  datadogRum.clearAccount()
  datadogRum.setTrackingConsent('not-granted')

  removeCookies(['_dd_s'])
}
